import { useEffect, useState } from "react";
import "./index.css";
import { Header } from "./components/Header";
import { ImageCarouselMinimapPage } from "./pages/carousel/ImageCarouselMinimapPage";
import { CvPage } from "./pages/CvPage";
import { CvPrintPage } from "./pages/CvPrintPage";
import { HomePage } from "./pages/HomePage";
import { MultimenuPage } from "./pages/multimenu/MultimenuPage";
import { PaperShaderPage } from "./pages/paper/PaperShaderPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { ToolsPage } from "./pages/ToolsPage";
import ContCamPage from "./pages/tools/ContCamPage";
import OmaWisperPage from "./pages/tools/OmaWisperPage";

function RouteContent({ pathname }: { pathname: string }) {
  switch (pathname) {
    case "/":
      return <HomePage />;
    case "/tools":
    case "/tools/":
      return <ToolsPage />;
    case "/tools/contcam":
    case "/tools/contcam/":
    case "/tools/omacam":
    case "/tools/omacam/":
      return <ContCamPage />;
    case "/tools/omawisper":
    case "/tools/omawisper/":
      return <OmaWisperPage />;
    case "/cv":
    case "/csv":
      return <CvPage />;
    case "/cv-print":
      return <CvPrintPage />;
    case "/components/paper":
      return <PaperShaderPage />;
    case "/components/image-coursel-minimap":
      return <ImageCarouselMinimapPage />;
    case "/components/multimenu":
      return <MultimenuPage />;
    default:
      return (
        <PlaceholderPage
          title="Page not found"
          subtitle="The requested page does not exist in this version."
          backHref="/"
        />
      );
  }
}

export function App() {
  const [pathname, setPathname] = useState(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );

  useEffect(() => {
    const resetScrollPosition = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target !== "_self") return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      if (/^\/tools\/(?:contcam|omacam)\/(?:support|privacy)(?:\.html)?\/?$/.test(url.pathname)) return;

      const currentUrl = new URL(window.location.href);
      if (
        url.pathname === currentUrl.pathname &&
        url.search === currentUrl.search &&
        url.hash
      ) {
        return;
      }

      event.preventDefault();

      if (url.href !== currentUrl.href) {
        window.history.pushState({}, "", url);
        setPathname(url.pathname);
      }

      resetScrollPosition();
    };

    const handlePopState = () => {
      setPathname(window.location.pathname);
      requestAnimationFrame(() => {
        const section = window.location.hash
          ? document.getElementById(window.location.hash.slice(1))
          : null;
        if (section) section.scrollIntoView();
        else resetScrollPosition();
      });
    };

    document.addEventListener("click", handleClick);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("popstate", handlePopState);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const themeToggleNeeded = pathname !== "/components/multimenu" && pathname !== "/cv-print";

  return (
    <div className="min-h-screen w-full">
      {themeToggleNeeded ? <Header /> : null}
      <div className="w-full">
        <RouteContent pathname={pathname} />
      </div>
    </div>
  );
}

export default App;
